import React from 'react';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useSearchParams } from 'react-router-dom';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useTranslation } from 'react-i18next';

import { useSearchSuggestLocationQuery, SearchSuggest } from '@graphql';

interface Option {
  label: string;
  type: 'city' | 'county' | 'postalCode';
  postalCode: string;
  county: string | null;
  city: string | null;
}

export const LocationFormField = () => {
  const [options, setOptions] = React.useState<readonly Option[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const theme = useTheme();

  const search = React.useMemo(() => {
    return searchParams.get('search')?.slice(0, -1);
  }, [searchParams]);

  const locationParam = searchParams.get('location') ?? '';

  const selected = React.useMemo(() => {
    const locations = locationParam ? locationParam.split('+') : [];
    return options.filter((option) => locations.includes(option.label));
  }, [options, locationParam]);

  const { data, loading } = useSearchSuggestLocationQuery({
    skip: !['spot', 'event'].includes(search ?? ''),
    variables: { search: search as SearchSuggest },
    fetchPolicy: 'cache-and-network',
  });

  const onErrors = (errors: any) => {};

  const handleGetLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(() => {
      const newOption: Option = {
        label: 'Pyrénées-Atlantiques',
        type: 'county',
        city: null,
        county: 'Pyrénées-Atlantiques',
        postalCode: '64',
      };

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('location', newOption.label);
      nextSearchParams.set('page', '1');

      setSearchParams(nextSearchParams, { replace: true });
    }, onErrors);
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: Option) => `${option.label}`,
    limit: 10,
    trim: true,
    ignoreAccents: true,
    ignoreCase: true,
  });

  React.useEffect(() => {
    if (loading) {
      return;
    }

    const locations = locationParam ? locationParam.split('+') : [];

    const formatted = (data?.searchSuggestLocation ?? []).reduce(
      (acc, curr) => {
        const { city, county, postalCode } = curr;
        const countyPostalCode = postalCode.slice(0, 2);

        if (!acc.cities.has(city)) {
          acc.cities.add(city);
          acc.options.push({ label: city, type: 'city', city, county, postalCode });
        }

        if (!acc.counties.has(county)) {
          acc.counties.add(county);
          acc.options.push({
            label: county,
            type: 'county',
            city: null,
            county,
            postalCode: countyPostalCode,
          });
        }

        if (!acc.potalCodes.has(countyPostalCode)) {
          acc.potalCodes.add(countyPostalCode);
          acc.options.push({
            label: countyPostalCode,
            type: 'postalCode',
            city: null,
            county,
            postalCode: countyPostalCode,
          });
        }

        if (!acc.potalCodes.has(postalCode)) {
          acc.potalCodes.add(postalCode);
          acc.options.push({
            label: postalCode,
            type: 'postalCode',
            city: null,
            county,
            postalCode,
          });
        }

        return acc;
      },
      {
        cities: new Set<string>(),
        counties: new Set<string | null>(),
        potalCodes: new Set<string>(),
        options: [] as Option[],
      }
    );

    setOptions(formatted.options);

    if (!locationParam) {
      return;
    }

    const selectedOptions = formatted.options.filter((option) => locations.includes(option.label));

    if (selectedOptions.length) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('location');
    nextSearchParams.set('page', '1');

    setSearchParams(nextSearchParams, { replace: true });
  }, [loading, data, locationParam, searchParams, setSearchParams]);

  return (
    <Autocomplete
      multiple
      fullWidth
      autoHighlight
      size="small"
      limitTags={2}
      id="multiple-location-limit-tags"
      popupIcon={<theme.icons.myLocation />}
      value={selected}
      slotProps={{
        popupIndicator: {
          onClick: handleGetLocation,
        },
      }}
      onChange={(event, newValue) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('location');

        if (newValue.length) {
          const location = newValue.map((option) => option.label.trim()).join('+');
          nextSearchParams.set('location', location);
          nextSearchParams.set('page', '1');
        }

        setSearchParams(nextSearchParams, { replace: true });
      }}
      sx={{
        width: '100%',
      }}
      options={options}
      noOptionsText={t('searchLocation.noOptionsText', { ns: 'field' })}
      getOptionLabel={(option) => option.label}
      filterOptions={(options, state) => {
        if (state.inputValue.trim().length === 0) {
          return [];
        }
        return filterOptions(options, state);
      }}
      renderOption={(props, option, { inputValue }) => {
        const { key, ...optionProps } = props;
        const matches = match(option.label, inputValue, { insideWords: true });
        const parts = parse(option.label, matches);

        return (
          <Box
            {...props}
            component="li"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{
                    fontWeight: part.highlight ? 700 : 400,
                  }}
                >
                  {part.text}
                </span>
              ))}
            </Box>
            <Typography variant="caption" sx={{ marginLeft: 'auto' }}>
              {t(`word.${option.type}`)}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder={selected.length ? '' : t('searchLocation.placeholder', { ns: 'field' })}
          slotProps={{
            input: {
              style: {
                width: '100%',
              },
              ...params.InputProps,
              autoComplete: 'off',
              type: 'search',
            },
          }}
        />
      )}
    />
  );
};
